import PortfolioAnalysisServiceCard from '@/features/portfolio-analysis/components/PortfolioAnalysisServiceCard';
import PortfolioAnalysisBuildingsDatagrid from '@/features/portfolio-analysis/components/datagrid/PortfolioAnalysisBuildingsDatagrid';
import PortfolioAnalysisDashboardDatagrid from '@/features/portfolio-analysis/components/datagrid/PortfolioAnalysisDashboardDatagrid';
import { meanByType } from '@/layout/pages/PortfolioAnalysis/utils/helpers';
import BoltIcon from '@mui/icons-material/Bolt';
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded';
import WaterDropRoundedIcon from '@mui/icons-material/WaterDropRounded';
import { Grid, Skeleton, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  portfolioAnalysisApiSlice,
  useGetBenchmarkingQuery,
  useGetBenchmarkOverwriteQuery,
  useGetPortfolioAnalysisHeatQuery,
  useGetPortfolioAnalysisPowerQuery,
  useGetPortfolioAnalysisSubscriptionQuery,
  useGetPortfolioAnalysisWaterQuery,
  useGetPortfolioHeatMetersQuery,
  useGetPortfolioPowerMetersQuery,
  useGetPortfolioWaterMetersQuery,
} from '../../../api/energy-analysis/portfolioAnalysisApiSlice';
import { getSelectedAccount } from '../../../redux/selectedAccountSlice';
import { showSnackbar } from '../../../redux/snackbarSlice';
import MeterContainer from './MeterContainer';

const PortfolioAnalysisDashboardPage = () => {
  const theme = useTheme();
  const location = useLocation();
  const { t } = useTranslation('portfolio-analysis_dashboard');
  const dispatch = useDispatch();

  const [isOpen, setIsOpen] = useState(false);
  const [selectedMeter, setSelectedMeter] = useState();

  const handleSetSelectedMeter = (meter) => {
    setIsOpen(true);
    setSelectedMeter(meter);
  };

  const [meterType, setMeterType] = useState('');
  const account = useSelector(getSelectedAccount);

  const {
    data: subscriptionData,
    isError: subscriptionError,
    isFetching: isSubscriptionFetching,
  } = useGetPortfolioAnalysisSubscriptionQuery(
    { recalc_account_id: account?.AccountId },
    { skip: !account?.AccountId },
  );

  const warned = useRef(false);

  useEffect(() => {
    if (subscriptionError && !warned.current) {
      warned.current = true;
      dispatch(
        showSnackbar({ message: t('noSubscriptions'), severity: 'warning' }),
      );
    }
  }, [subscriptionError, dispatch, t]);

  const { data: powerData, isFetching: isPowerFetching } =
    useGetPortfolioAnalysisPowerQuery(
      { recalc_account_id: account?.AccountId },
      { skip: !account?.AccountId || !subscriptionData?.power },
    );
  const { data: waterData, isFetching: isWaterFetching } =
    useGetPortfolioAnalysisWaterQuery(
      { recalc_account_id: account?.AccountId },
      { skip: !account?.AccountId || !subscriptionData?.water },
    );
  const { data: heatData, isFetching: isHeatFetching } =
    useGetPortfolioAnalysisHeatQuery(
      { recalc_account_id: account?.AccountId },
      { skip: !account?.AccountId || !subscriptionData?.heat },
    );
  const { data: powerMeterData } = useGetPortfolioPowerMetersQuery(
    { recalc_account_id: account?.AccountId },
    {
      skip: !account?.AccountId,
      pollingInterval: 60000,
      skipPollingIfUnfocused: true,
    },
  );
  const { data: waterMeterData } = useGetPortfolioWaterMetersQuery(
    { recalc_account_id: account?.AccountId },
    {
      skip: !account?.AccountId,
      pollingInterval: 60000,
      skipPollingIfUnfocused: true,
    },
  );
  const { data: heatMeterData } = useGetPortfolioHeatMetersQuery(
    { recalc_account_id: account?.AccountId },
    {
      skip: !account?.AccountId,
      pollingInterval: 60000,
      skipPollingIfUnfocused: true,
    },
  );
  const isDatagridFetching = useMemo(
    () =>
      isPowerFetching ||
      isWaterFetching ||
      isHeatFetching ||
      isSubscriptionFetching,
    [isPowerFetching, isWaterFetching, isHeatFetching, isSubscriptionFetching],
  );
  const { data: benchmarkData } = useGetBenchmarkingQuery(
    { recalc_account_id: account?.AccountId },
    { skip: !account?.AccountId },
  );
  const { data: benchmarkOverwriteData } = useGetBenchmarkOverwriteQuery(
    { account_id: account?.AccountId },
    { skip: !account?.AccountId },
  );

  const rows = useMemo(() => {
    let tempHeat = heatData?.portfolio_analysis_list
      ? [
          ...heatData.portfolio_analysis_list.map((o) => {
            let meterEntry =
              heatMeterData?.find(
                (b) => `${b.EjerlavKode}_${b.MatrikelNr}` == o.matrikel_key,
              ) || {};
            return {
              heat_total_consumption_year: o.total_consumption_year,
              type: 'heat',
              ...o,
              ...meterEntry,
              metering_point_id: JSON.parse(o.data_stream).unit_id,
            };
          }),
        ]
      : [];
    let tempPower = powerData?.portfolio_analysis_list
      ? [
          ...powerData.portfolio_analysis_list.map((o) => {
            let meterEntry =
              powerMeterData?.find(
                (b) => `${b.EjerlavKode}_${b.MatrikelNr}` == o.matrikel_key,
              ) || {};
            return {
              power_total_consumption_year: o.total_consumption_year,
              type: 'power',
              ...o,
              ...meterEntry,
            };
          }),
        ]
      : [];
    let tempWater = waterData?.portfolio_analysis_list
      ? [
          ...waterData.portfolio_analysis_list.map((o) => {
            let meterEntry =
              waterMeterData?.find(
                (b) => `${b.EjerlavKode}_${b.MatrikelNr}` == o.matrikel_key,
              ) || {};
            return {
              water_total_consumption_year: o.total_consumption_year,
              type: 'water',
              ...o,
              ...meterEntry,
              metering_point_id: JSON.parse(o.data_stream).unit_id,
            };
          }),
        ]
      : [];
    let list = [...tempHeat, ...tempPower, ...tempWater].filter(
      (item) => item.matrikel_key,
    );
    //list = list.filter((item) => ensureAllTypes(item, list));
    return { portfolio_analysis_list: list };
  }, [
    heatData,
    powerData,
    waterData,
    heatMeterData,
    powerMeterData,
    waterMeterData,
  ]);

  const [anyMeterOutOfSync, setAnyMeterOutOfSync] = useState(false);

  useEffect(() => {
    if (rows.portfolio_analysis_list.some((o) => !o.meter_in_sync)) {
      setAnyMeterOutOfSync(true);
    } else if (anyMeterOutOfSync) {
      setAnyMeterOutOfSync(false);
      portfolioAnalysisApiSlice.util.invalidateTags(['PortfolioAnalysis']);
    }
  }, [anyMeterOutOfSync, rows.portfolio_analysis_list]);

  const enrichedData = useMemo(() => {
    if (rows != null && benchmarkData == null) return rows;
    if (rows == null || benchmarkData == null) return null;
    let temp = {
      ...rows,
      portfolio_analysis_list: rows.portfolio_analysis_list.map((o) => {
        let benchmarkEntry = benchmarkData.BenchmarkData.find(
          (b) => b.MatrikelKey == o?.matrikel_key,
        ) || { SumBuildingArea: 0, AverageBuildingYear: 0, kWhPrYPrM2: 0 };
        let benchmarkOverwriteEntry =
          benchmarkOverwriteData?.find(
            (b) => b.MatrikelKey == o?.matrikel_key,
          ) || {};
        return { ...o, ...benchmarkEntry, overwrite: benchmarkOverwriteEntry };
      }),
    };
    return temp;
  }, [rows, benchmarkData, benchmarkOverwriteData]);

  return (
    <>
      <Grid container spacing={2} maxHeight={'80vh'} py={2}>
        <Suspense fallback={<Skeleton />}>
          {subscriptionData &&
            [
              {
                disabled: !subscriptionData?.power,
                label: t('cards.power.title'),
                path: '/portfolio-analysis-hub/power/overview',
                value: enrichedData.portfolio_analysis_list.reduce(
                  (acc, o) =>
                    o.power_total_consumption_year
                      ? acc + o.power_total_consumption_year
                      : acc,
                  0,
                ),
                unit: t('cards.power.unit'),
                icon: <BoltIcon />,
                color: theme.palette.power.main,
                dataPct: meanByType(
                  enrichedData.portfolio_analysis_list,
                  'power',
                  'one_year_data_pct',
                ),
                area: enrichedData.portfolio_analysis_list.reduce(
                  (acc, o) =>
                    o.SumBuildingArea && o.type === 'power'
                      ? acc + o.SumBuildingArea
                      : acc,
                  0,
                ),
                buildingCount: new Set(
                  enrichedData.portfolio_analysis_list
                    .filter((item) => item.type === 'power')
                    .map((item) => item.main_address),
                ).size,
                summedData: powerData?.summed_data ?? [],
              },
              {
                disabled: !subscriptionData?.heat,
                label: t('cards.heat.title'),
                path: '/portfolio-analysis-hub/heat/overview',
                value: enrichedData.portfolio_analysis_list.reduce(
                  (acc, o) =>
                    o.heat_total_consumption_year
                      ? acc + o.heat_total_consumption_year
                      : acc,
                  0,
                ),
                unit: t('cards.heat.unit'),
                icon: <LocalFireDepartmentRoundedIcon />,
                color: theme.palette.heat.main,
                area: enrichedData.portfolio_analysis_list.reduce(
                  (acc, o) =>
                    o.SumBuildingArea && o.type === 'heat'
                      ? acc + o.SumBuildingArea
                      : acc,
                  0,
                ),
                dataPct: meanByType(
                  enrichedData.portfolio_analysis_list,
                  'heat',
                  'one_year_data_pct',
                ),
                buildingCount: new Set(
                  enrichedData.portfolio_analysis_list
                    .filter((item) => item.type === 'heat')
                    .map((item) => item.main_address),
                ).size,
                summedData: heatData?.summed_data ?? [],
              },
              {
                disabled: !subscriptionData?.water,
                label: t('cards.water.title'),
                path: '/portfolio-analysis-hub/water/overview',
                value: enrichedData.portfolio_analysis_list.reduce(
                  (acc, o) =>
                    o.water_total_consumption_year
                      ? acc + o.water_total_consumption_year
                      : acc,
                  0,
                ),
                unit: t('cards.water.unit'),
                icon: <WaterDropRoundedIcon />,
                color: theme.palette.water.main,
                dataPct: meanByType(
                  enrichedData.portfolio_analysis_list,
                  'water',
                  'one_year_data_pct',
                ),
                area: enrichedData.portfolio_analysis_list.reduce(
                  (acc, o) =>
                    o.SumBuildingArea && o.type === 'water'
                      ? acc + o.SumBuildingArea
                      : acc,
                  0,
                ),
                buildingCount: new Set(
                  enrichedData.portfolio_analysis_list
                    .filter((item) => item.type === 'water')
                    .map((item) => item.main_address),
                ).size,
                summedData: waterData?.summed_data ?? [],
              },
            ].map((service) => (
              <Grid item md={12} lg={4} key={service.label}>
                <PortfolioAnalysisServiceCard {...service} />
              </Grid>
            ))}
        </Suspense>
        <Grid item xs={12} maxHeight={'80vh'}>
          <Suspense
            fallback={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  height: '80vh',
                  verticalAlign: 'center',
                }}
              >
                <CircularProgress />
              </Box>
            }
          >
            <Box
              minHeight={'50vh'}
              maxHeight={'60vh'}
              display={'flex'}
              flexDirection={'column'}
            >
              {location.pathname.includes('building') ? (
                <PortfolioAnalysisBuildingsDatagrid
                  data={enrichedData}
                  setSelectedMeter={handleSetSelectedMeter}
                  setMeterType={setMeterType}
                  outOfSync={false}
                  isLoading={isDatagridFetching}
                />
              ) : (
                <PortfolioAnalysisDashboardDatagrid
                  data={enrichedData}
                  setSelectedMeter={handleSetSelectedMeter}
                  setMeterType={setMeterType}
                  outOfSync={false}
                  isLoading={isDatagridFetching}
                />
              )}
            </Box>
          </Suspense>
        </Grid>
      </Grid>
      {selectedMeter && (
        <MeterContainer
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          meter={selectedMeter}
          meterType={meterType}
          analysisType={selectedMeter?.type}
        />
      )}
    </>
  );
};

export default PortfolioAnalysisDashboardPage;
